import {size, type Placement} from '@floating-ui/vue'
import {getPlacementSide} from './getPlacementSide'
import {getReferenceAnchoredAvailableSize} from './getReferenceAnchoredAvailableSize'

export type ReferenceAnchoredSizeApply = (space: {
  availableHeight: number
  availableWidth: number
}) => void

/**
 * Caps the floating element to the gap between the reference and the viewport
 * on the placed side (never over the trigger). Uses Floating UI `size()` /
 * `detectOverflow`, then clamps with reference-anchored limits.
 *
 * `isSelfSized` marks a panel that caps its own height in CSS. Clamping such
 * a panel to the gap next to the trigger would cut it off, so it only gets the
 * viewport as an upper bound and `shift` places it.
 *
 * @see https://floating-ui.com/docs/size
 * @see https://floating-ui.com/docs/detectOverflow
 */
export function createReferenceAnchoredSizeMiddleware(options: {
  padding?: number
  offsetMainAxis?: number
  isSelfSized?: boolean
  onApply?: ReferenceAnchoredSizeApply
}) {
  const padding = options.padding ?? 8
  const offsetMainAxis = options.offsetMainAxis ?? 0

  return size({
    padding,
    rootBoundary: 'viewport',
    apply({availableHeight, availableWidth, elements, rects, placement}) {
      const anchored = getReferenceAnchoredAvailableSize(
        rects.reference,
        placement as Placement,
        padding,
        offsetMainAxis,
      )
      const side = getPlacementSide(placement as Placement)

      // Main axis: only the [trigger → viewport] gap.
      // Cross axis (top/bottom width): full viewport budget — shift keeps x in view.
      const nextH = options.isSelfSized
        ? Math.max(window.innerHeight - padding * 2, 0)
        : Math.min(Math.max(availableHeight, 0), anchored.availableHeight)
      const nextW = side === 'top' || side === 'bottom'
        ? anchored.availableWidth
        : Math.min(Math.max(availableWidth, 0), anchored.availableWidth)

      elements.floating.style.maxHeight = `${nextH}px`
      elements.floating.style.maxWidth = `${nextW}px`
      elements.floating.style.setProperty('--dropdown-available-height', `${nextH}px`)
      elements.floating.style.setProperty('--dropdown-available-width', `${nextW}px`)
      options.onApply?.({
        availableHeight: nextH,
        availableWidth: nextW,
      })
    },
  })
}
